import { Component, inject, signal, DestroyRef, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { I18nTranslatePipe } from '../../core/pipes/i18n-translate.pipe';
import { I18nService } from '../../core/services/i18n.service';
import { CommonModule } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import { ToasterService } from '../../shared/toaster/toaster.service';
import { ApiService } from '../../core/services/api.service';
import { Shared } from '../../core/services/shared';
import {
  agentEmail,
  agentId,
  agentName,
  agentMobileNumber,
  clientToken,
  leadOwnerAgentId,
} from '../../core/constants/auth-state';
import { UtilsService } from '../../core/services/utils.service';
import { FaqModule } from '../../core/constants/faq.constants';
import { catchError, of } from 'rxjs';
import { AgentContactService } from '../../core/services/agent-contact.service';

@Component({
  selector: 'app-faq',
  imports: [
    CommonModule,
    MatCardModule,
    MatExpansionModule,
    MatIconModule,
    I18nTranslatePipe,
  ],
  templateUrl: './faq.html',
  styleUrl: './faq.scss',
})
export class Faq implements OnInit {
  protected readonly i18n = inject(I18nService);
  private readonly clipboard = inject(Clipboard);
  private readonly toaster = inject(ToasterService);
  private readonly apiService = inject(ApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly shared = inject(Shared);
  private readonly utilsService = inject(UtilsService);
  private readonly agentContactService = inject(AgentContactService);

  protected readonly isFaqOpen = signal(false);
  protected readonly isFaqExpanded = signal(false);

  readonly agentPhone = signal('+1 234 567 890');
  readonly agentEmail = signal('support@example.com');
  agentDetails: any = null;
  faqs: any[] = [];
  private allFaqs: any[] = [];
  private currentIndex = 0;
  protected readonly isLoading = signal(false);

  private readonly COLLAPSED_BATCH = 5;
  private readonly EXPANDED_BATCH = 5;
  private readonly EXPANDED_INITIAL_BATCH = 10;
  private readonly agentPlaceholder = {
    id: '',
    userName: 'Support Team',
    emailId: 'support@example.com',
    mobileNumber: '+1 234 567 890',
  };

  ngOnInit(): void {
    this.setAgentContactDetails();
    this.loadPreferenceFaqs();
  }

  toggleFaq(): void {
    this.isFaqOpen.update((value) => {
      const next = !value;
      if (!next && this.isFaqExpanded()) {
        this.isFaqExpanded.set(false);
      }
      this.resetFaqs();
      return next;
    });
  }

  expandFaq(): void {
    const isExpanding = !this.isFaqExpanded();
    this.isFaqExpanded.set(isExpanding);

    if (isExpanding) {
      // Preload more so expanded view isn't empty
      const missing = this.EXPANDED_INITIAL_BATCH - this.faqs.length;

      if (missing > 0) {
        this.loadMoreFaqs(missing);
      }
    }
  }

  copyAndCall(): void {
    const phone = this.agentPhone();
    this.clipboard.copy(phone);
    this.toaster?.show({
      message: 'Phone Number copied to clipboard',
      status: 'success',
    });
  }

  copyAndEmail(): void {
    const email = this.agentEmail();
    this.clipboard.copy(email);
    this.toaster?.show({
      message: 'Email copied to clipboard',
      status: 'success',
    });
    this.shared.setIntentionalNavigation(true);
    this.utilsService.navigateToExternalUrl(`mailto:${email}`);
  }

  private loadPreferenceFaqs(): void {
    this.apiService.subscribeWithCleanup(
      this.apiService.getPreferenceFaqs(FaqModule.PreferenceForm).pipe(
        catchError(() => of({ body: [] })),
      ),
      (response: unknown) => {
        const faqs = this.extractFaqItems(response);
        this.allFaqs = faqs;
        this.currentIndex = 0;
        this.faqs = [];

        this.loadMoreFaqs();
      },
      this.destroyRef,
    );
  }

  private extractFaqItems(response: unknown): Array<{ question: string; answer: string }> {
    const body = this.getObjectProperty(response, 'body');

    if (!Array.isArray(body)) {
      return [];
    }

    return body
      .filter((item: unknown): item is Record<string, unknown> => this.isRecord(item))
      .map((item: Record<string, unknown>) => ({
        question: this.toText(item['question']),
        answer: this.toText(item['answer']),
      }))
      .filter((item) => item.question.length > 0 && item.answer.length > 0);
  }

  private setAgentContactDetails(): void {
    if (!clientToken()) {
      this.applyAgentContactDetails(this.agentPlaceholder);
      this.agentContactService.loadAgentContact(leadOwnerAgentId()).subscribe((agent) => {
        if (!agent) return;
        this.applyAgentContactDetails({
          id: agent.id,
          userName: `${agent.firstName} ${agent.lastName}`.trim(),
          emailId: agent.emailId,
          mobileNumber: agent.mobileNumber,
        });
      });
      return;
    }

    this.applyAgentContactDetails({
      id: agentId(),
      userName: agentName(),
      emailId: agentEmail(),
      mobileNumber: agentMobileNumber(),
    });
  }

  private applyAgentContactDetails(agent: {
    id?: string | null;
    userName?: string | null;
    emailId?: string | null;
    mobileNumber?: string | null;
  }): void {
    const resolvedAgent = {
      id: this.toText(agent.id),
      userName: this.toText(agent.userName) || this.agentPlaceholder.userName,
      emailId: this.toText(agent.emailId) || this.agentPlaceholder.emailId,
      mobileNumber:
        this.toText(agent.mobileNumber) || this.agentPlaceholder.mobileNumber,
    };

    this.agentDetails = resolvedAgent;
    this.agentEmail.set(resolvedAgent.emailId);
    this.agentPhone.set(resolvedAgent.mobileNumber);
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private getObjectProperty(source: unknown, key: string): unknown {
    if (!this.isRecord(source)) {
      return null;
    }
    return source[key];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private loadMoreFaqs(batchOverride?: number): void {
    if (this.currentIndex >= this.allFaqs.length) return;

    this.isLoading.set(true);

    const batchSize =
      batchOverride ??
      (this.isFaqExpanded() ? this.EXPANDED_BATCH : this.COLLAPSED_BATCH);

    const nextChunk = this.allFaqs.slice(
      this.currentIndex,
      this.currentIndex + batchSize,
    );

    this.faqs = [...this.faqs, ...nextChunk];
    this.currentIndex += batchSize;

    this.isLoading.set(false);
  }

  onFaqScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const threshold = 80;

    if (
      target.scrollTop + target.clientHeight >=
      target.scrollHeight - threshold
    ) {
      this.loadMoreFaqs();
    }
  }

  private resetFaqs(): void {
    this.faqs = [];
    this.currentIndex = 0;

     if (this.allFaqs.length > 0) {
       this.loadMoreFaqs(this.COLLAPSED_BATCH);
     }
  }
}
